/**
 * AVBP live tasks store — the REAL top-level `tasks/` Firestore collection.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY (Build B · 2026-05-21, Anil)
 * ─────────────────────────────────────────────────────────────────────────
 *  The conversational AI assistant — in-app (`avbp_chat_extract.js`
 *  `inAppExecuteLow`) AND WhatsApp (`avbp_wa_router.js` `executeLowRisk`) —
 *  CREATES real `tasks/` documents via the shared `StaffResolver` contract.
 *  But NO portal screen ever read them: the Home "YOUR TASKS" panel was fed
 *  by `lib/tasks.ts`, which is 100% hardcoded demo data with no Firebase.
 *
 *  This module is the live data layer the task-oversight screen, the Home
 *  panel and the Workload screen consume. It mirrors the pure/impure split
 *  of `projectStore.ts` / `staffRoles.ts`:
 *    · `taskRowFrom`, `groupByAssignee`, `tasksForUid`, `countByAssignee`,
 *      `openCount` are PURE + unit-tested (no React, no Firebase).
 *    · `listenTasks` is the thin Firestore realtime seam.
 *
 *  TASK DOC SHAPE (written by the agent executors):
 *    { title, status:'open'|'done'|…, priority, assigneeUid, assigneeName,
 *      source, createdVia, byUid, surface, at }
 *  Older / WhatsApp-origin docs may omit some fields — every mapper is
 *  defensive (sensible fallbacks, never throws on a partial doc).
 * ─────────────────────────────────────────────────────────────────────────
 */
import {
  collection,
  onSnapshot,
  query,
  Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import type { ListenOpts } from './projectStore';

/* ════════════════════ Model ════════════════════ */

/** Task lifecycle status. `open` is the live default; any other → done-ish. */
export type TaskStatus = 'open' | 'done' | string;

/** Priority band — matches the agent executor's `priority` field. */
export type LiveTaskPriority = 'high' | 'normal' | 'low';

/** A live `tasks/` row, normalised. */
export interface LiveTask {
  /** tasks/ doc id. */
  id: string;
  title: string;
  status: TaskStatus;
  priority: LiveTaskPriority;
  /** Real staff uid the StaffResolver matched, or '' when unassigned. */
  assigneeUid: string;
  assigneeName: string;
  /** Origin tag — 'agent_inapp' | 'agent_wa' | 'avbp_agent' | … */
  source: string;
  /** How the doc was created — 'avbp_agent_inapp' | 'avbp_wa_router' | … */
  createdVia: string;
  /** uid of the person whose instruction created the task (the agent caller). */
  byUid: string;
  /** Capture surface — 'portal_chat' | 'whatsapp' | … */
  surface: string;
  /** Creation time (epoch ms) — null until serverTimestamp resolves. */
  atMs: number | null;
}

/** Constant used everywhere a missing assignee must read as one bucket. */
export const UNASSIGNED_UID = '';
export const UNASSIGNED_LABEL = 'Unassigned';

/* ════════════════════ PURE mappers ════════════════════ */

function priorityOf(raw: unknown): LiveTaskPriority {
  const v = String(raw ?? '').toLowerCase();
  return v === 'high' || v === 'low' ? v : 'normal';
}

/** epoch-ms from a Firestore Timestamp | number | null — defensively. */
function atMsOf(raw: unknown): number | null {
  if (raw instanceof Timestamp) return raw.toMillis();
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  // Plain object shaped like a Timestamp (test fixtures / cached docs).
  if (raw && typeof raw === 'object' && 'seconds' in (raw as object)) {
    const s = (raw as { seconds?: unknown }).seconds;
    if (typeof s === 'number') return s * 1000;
  }
  return null;
}

/**
 * PURE — map a raw `tasks/` doc to a `LiveTask`. Never throws on a partial
 * doc: a WhatsApp-origin task or an early-schema doc may omit fields, so
 * every field has a sensible fallback. An assignee uid that is missing or
 * blank normalises to `UNASSIGNED_UID` ('') so grouping has one bucket.
 */
export function taskRowFrom(
  id: string,
  d: Record<string, unknown>,
): LiveTask {
  const assigneeUid =
    typeof d.assigneeUid === 'string' && d.assigneeUid.trim()
      ? d.assigneeUid.trim()
      : UNASSIGNED_UID;
  return {
    id,
    title: typeof d.title === 'string' && d.title ? d.title : 'Untitled task',
    status:
      typeof d.status === 'string' && d.status ? d.status : 'open',
    priority: priorityOf(d.priority),
    assigneeUid,
    assigneeName:
      typeof d.assigneeName === 'string' && d.assigneeName.trim()
        ? d.assigneeName.trim()
        : UNASSIGNED_LABEL,
    source: typeof d.source === 'string' ? d.source : '',
    createdVia: typeof d.createdVia === 'string' ? d.createdVia : '',
    byUid: typeof d.byUid === 'string' ? d.byUid : '',
    surface: typeof d.surface === 'string' ? d.surface : '',
    atMs: atMsOf(d.at),
  };
}

/** PURE — is this task still open (live work)? */
export function isOpenTask(t: { status: TaskStatus }): boolean {
  return String(t.status).toLowerCase() === 'open';
}

/** PURE — only the OPEN tasks. */
export function openTasks(tasks: LiveTask[]): LiveTask[] {
  return tasks.filter(isOpenTask);
}

const PRIORITY_RANK: Record<LiveTaskPriority, number> = {
  high: 0,
  normal: 1,
  low: 2,
};

/**
 * PURE — sort a task list for display: open before done, then priority
 * (high → low), then newest first, then a stable id tie-break. Deterministic.
 */
export function sortTasks(tasks: LiveTask[]): LiveTask[] {
  return tasks.slice().sort((a, b) => {
    const oa = isOpenTask(a) ? 0 : 1;
    const ob = isOpenTask(b) ? 0 : 1;
    if (oa !== ob) return oa - ob;
    const pa = PRIORITY_RANK[a.priority];
    const pb = PRIORITY_RANK[b.priority];
    if (pa !== pb) return pa - pb;
    const ta = a.atMs ?? 0;
    const tb = b.atMs ?? 0;
    if (ta !== tb) return tb - ta;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

/**
 * PURE — the tasks assigned to ONE real staff uid, display-sorted. Used by
 * the Home "Your tasks" panel (filtered to the signed-in user's uid). An
 * empty / null uid returns the genuinely-unassigned bucket.
 */
export function tasksForUid(
  tasks: LiveTask[],
  uid: string | null | undefined,
): LiveTask[] {
  const want = (uid ?? '').trim();
  return sortTasks(tasks.filter((t) => t.assigneeUid === want));
}

/** One assignee's grouped task bucket. */
export interface AssigneeGroup {
  assigneeUid: string;
  assigneeName: string;
  tasks: LiveTask[];
  /** Count of OPEN tasks in this bucket (the workload signal). */
  openCount: number;
}

/**
 * PURE — fold a flat task list into one group per assignee, each group's
 * tasks display-sorted. Groups are ordered by open-count descending (the
 * busiest assignee first) so the oversight screen + Workload surface where
 * work is piling up; the Unassigned bucket sinks to the end.
 */
export function groupByAssignee(tasks: LiveTask[]): AssigneeGroup[] {
  const byUid = new Map<string, AssigneeGroup>();
  for (const t of tasks) {
    let g = byUid.get(t.assigneeUid);
    if (!g) {
      g = {
        assigneeUid: t.assigneeUid,
        assigneeName: t.assigneeName,
        tasks: [],
        openCount: 0,
      };
      byUid.set(t.assigneeUid, g);
    }
    g.tasks.push(t);
    // A real assignee name on any of their tasks wins over the placeholder.
    if (g.assigneeName === UNASSIGNED_LABEL && t.assigneeUid !== UNASSIGNED_UID) {
      g.assigneeName = t.assigneeName;
    }
  }
  const groups = Array.from(byUid.values());
  for (const g of groups) {
    g.tasks = sortTasks(g.tasks);
    g.openCount = g.tasks.filter(isOpenTask).length;
  }
  groups.sort((a, b) => {
    // Unassigned bucket always last.
    const ua = a.assigneeUid === UNASSIGNED_UID ? 1 : 0;
    const ub = b.assigneeUid === UNASSIGNED_UID ? 1 : 0;
    if (ua !== ub) return ua - ub;
    if (b.openCount !== a.openCount) return b.openCount - a.openCount;
    return a.assigneeName < b.assigneeName ? -1 : 1;
  });
  return groups;
}

/**
 * PURE — open-task count per assignee, as a plain map keyed by uid. The
 * Workload screen reads this for its real per-assignee task dimension.
 */
export function countByAssignee(tasks: LiveTask[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const t of openTasks(tasks)) {
    m.set(t.assigneeUid, (m.get(t.assigneeUid) ?? 0) + 1);
  }
  return m;
}

/** PURE — total OPEN-task count across every assignee. */
export function openCount(tasks: LiveTask[]): number {
  return openTasks(tasks).length;
}

/* ════════════════════ Roster-anchored workload ════════════════════
 *
 * BUG 2 (2026-05-22) — the Workload screen built its people list purely
 * from `groupByAssignee` over the live `tasks/` docs, so it showed ONLY
 * whoever owned a task — never the real staff roster. A staffer with zero
 * tasks was invisible; loose non-staff-id assignee strings (e.g. `Kunjal`
 * coming from the WhatsApp admin-command resolver before it matched a
 * staff doc) showed up as phantom "people".
 *
 * `rosterWorkload` is the fix: it anchors on the ACTIVE staff roster (the
 * SAME `AvbpStaffRow[]` `SettingsStaffRoles` / `listenAvbpStaff` use) and
 * attaches each staffer's real task load — 0 if they own none. A loose
 * assignee string is resolved to the matching real staff member by name
 * where possible; an unresolvable one is grouped as a clearly-labelled
 * "unmatched" bucket, never a phantom person. `Unassigned` stays its own
 * bucket. PURE — no React, no Firebase — unit-tested.
 */

/** The minimal staff shape `rosterWorkload` needs (a subset of AvbpStaffRow). */
export interface RosterMember {
  /** staff/ doc id — e.g. E016. This is what a resolved `assigneeUid` holds. */
  id: string;
  name: string;
  active: boolean;
}

/** What a `rosterWorkload` row is — a staff member OR a non-staff bucket. */
export type WorkloadRowKind = 'staff' | 'unassigned' | 'unmatched';

/** One row of the roster-anchored workload view. */
export interface WorkloadRow {
  /** Stable react key — staff id, '' for Unassigned, or 'unmatched:<raw>'. */
  key: string;
  kind: WorkloadRowKind;
  /** Display name — the staff name, 'Unassigned', or the loose raw string. */
  name: string;
  tasks: LiveTask[];
  openCount: number;
}

/**
 * PURE — canonical comparison form of a person name. Mirrors the functions
 * `staffResolver.normalizeName`: lower-cased, diacritics stripped,
 * punctuation collapsed to spaces. Kept local (the portal must not import
 * a Cloud-Functions module) — same contract, used only for the
 * best-effort loose-string → staff resolution below.
 */
export function normalizeAssigneeName(raw: unknown): string {
  return String(raw == null ? '' : raw)
    .normalize('NFKD')
    // eslint-disable-next-line no-misleading-character-class
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * PURE — best-effort resolve a loose assignee identifier (a uid that is NOT
 * a known staff id, or an assignee name string) to a real staff member.
 *
 * Conservative — a WRONG attribution is worse than an honest "unmatched":
 *   - exact normalised name equality on a staff member        → that member
 *   - the loose string equals a staff member's FIRST token,   → that member
 *     and EXACTLY ONE staffer satisfies it (ambiguous → none)
 *   - otherwise                                               → null
 *
 * Returns the matched `RosterMember`, or `null` when no confident match.
 */
export function resolveLooseAssignee(
  loose: string,
  staff: RosterMember[],
): RosterMember | null {
  const want = normalizeAssigneeName(loose);
  if (!want) return null;
  // Pass 1 — exact normalised name equality.
  const exact = staff.filter((s) => normalizeAssigneeName(s.name) === want);
  if (exact.length === 1) return exact[0];
  if (exact.length > 1) return null; // ambiguous — no confident match
  // Pass 2 — first-token match (user said "Kunjal", staff is "Kunjal Shah"),
  // only when EXACTLY ONE staffer satisfies it.
  const firstTokenHits = staff.filter((s) => {
    const tokens = normalizeAssigneeName(s.name).split(' ').filter(Boolean);
    return tokens.length > 0 && tokens[0] === want;
  });
  return firstTokenHits.length === 1 ? firstTokenHits[0] : null;
}

/**
 * PURE — the roster-anchored workload. EVERY active staff member becomes a
 * row (0 open tasks if they own none); loose non-staff-id assignee strings
 * are folded into the matching staffer where confidently resolvable, else
 * grouped under their own clearly-labelled "unmatched" rows; the genuinely-
 * unassigned bucket is its own row.
 *
 * Row order: staff rows first, busiest (open count) first, then by name;
 * then any unmatched buckets (busiest first); the Unassigned bucket always
 * last. An inactive staff member appears ONLY if they still own tasks (so
 * work is never hidden) — flagged via `kind` so the screen can mark them.
 */
export function rosterWorkload(
  tasks: LiveTask[],
  staff: RosterMember[],
): WorkloadRow[] {
  const activeStaff = staff.filter((s) => s.active);
  const staffById = new Map<string, RosterMember>();
  for (const s of staff) staffById.set(s.id, s);

  // Seed a row for every ACTIVE staff member — 0 tasks until one lands.
  const rows = new Map<string, WorkloadRow>();
  for (const s of activeStaff) {
    rows.set(s.id, {
      key: s.id,
      kind: 'staff',
      name: s.name || s.id,
      tasks: [],
      openCount: 0,
    });
  }
  // The Unassigned bucket — always present so "nobody owns this" is visible.
  rows.set(UNASSIGNED_UID, {
    key: UNASSIGNED_UID,
    kind: 'unassigned',
    name: UNASSIGNED_LABEL,
    tasks: [],
    openCount: 0,
  });

  for (const t of tasks) {
    const uid = t.assigneeUid;
    if (uid === UNASSIGNED_UID) {
      rows.get(UNASSIGNED_UID)!.tasks.push(t);
      continue;
    }
    // A real staff-id assignee — fold straight onto that staffer's row
    // (creating it for an INACTIVE staffer who still owns tasks).
    const known = staffById.get(uid);
    if (known) {
      let row = rows.get(known.id);
      if (!row) {
        row = {
          key: known.id,
          kind: 'staff',
          name: known.name || known.id,
          tasks: [],
          openCount: 0,
        };
        rows.set(known.id, row);
      }
      row.tasks.push(t);
      continue;
    }
    // A loose non-staff-id string (e.g. `Kunjal` / a typo `Kunjun`). Try to
    // resolve it to a real staffer by name; fall onto their row if matched.
    const resolved = resolveLooseAssignee(uid, staff) ??
      resolveLooseAssignee(t.assigneeName, staff);
    if (resolved) {
      let row = rows.get(resolved.id);
      if (!row) {
        row = {
          key: resolved.id,
          kind: 'staff',
          name: resolved.name || resolved.id,
          tasks: [],
          openCount: 0,
        };
        rows.set(resolved.id, row);
      }
      row.tasks.push(t);
      continue;
    }
    // Unresolvable — group under a clear "unmatched" bucket keyed by the
    // raw value so it is NEVER mistaken for a real person.
    const key = `unmatched:${uid}`;
    let row = rows.get(key);
    if (!row) {
      row = {
        key,
        kind: 'unmatched',
        name: t.assigneeName !== UNASSIGNED_LABEL ? t.assigneeName : uid,
        tasks: [],
        openCount: 0,
      };
      rows.set(key, row);
    }
    row.tasks.push(t);
  }

  const out = Array.from(rows.values());
  for (const r of out) {
    r.tasks = sortTasks(r.tasks);
    r.openCount = r.tasks.filter(isOpenTask).length;
  }
  const KIND_RANK: Record<WorkloadRowKind, number> = {
    staff: 0,
    unmatched: 1,
    unassigned: 2,
  };
  out.sort((a, b) => {
    if (a.kind !== b.kind) return KIND_RANK[a.kind] - KIND_RANK[b.kind];
    if (b.openCount !== a.openCount) return b.openCount - a.openCount;
    return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
  });
  return out;
}

/* ════════════════════ Firestore seam (impure) ════════════════════ */

/**
 * Live realtime list of the REAL top-level `tasks/` collection. Single
 * unbounded `onSnapshot` (no `where`/`orderBy` → no composite index
 * needed; client-side sort/group via the pure mappers above). Mirrors
 * `listenProjects`: the 2-arg `(cb, onErr)` form is back-compat; the
 * preferred form passes a `ListenOpts` object so a screen can also learn
 * when the first snapshot has arrived (`onReady`) — the three-state load
 * model (loading → empty → rows).
 */
export function listenTasks(
  cb: (tasks: LiveTask[]) => void,
  optsOrErr?: ListenOpts | ((e: Error) => void),
): Unsubscribe {
  const opts: ListenOpts =
    typeof optsOrErr === 'function' ? { onErr: optsOrErr } : optsOrErr ?? {};
  let readyFired = false;
  const fireReady = () => {
    if (!readyFired) {
      readyFired = true;
      opts.onReady?.();
    }
  };
  return onSnapshot(
    query(collection(db, 'tasks')),
    (snap) => {
      cb(snap.docs.map((dd) => taskRowFrom(dd.id, dd.data())));
      fireReady();
    },
    (e) => {
      opts.onErr?.(e);
      fireReady();
    },
  );
}
